import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { TileCarousel } from 'components/TileCarousel/TileCarousel';
import { UnpublishedListing } from 'components/UnpublishedListing/UnpublishedListing';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useI18n } from 'i18n/hooks/useI18n';
import type { ApiUnpublishedJob } from 'modules/listing/jobs/api/types/ApiUnpublishedJob';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from 'modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetJobsRoutePath } from 'modules/search/routing/helpers/searchGetJobsRoutePath';
import { trackListing } from 'utils/analytics/trackListing';

import { getUnpublishedJobPageSeeMoreText } from './getUnpublishedJobPageSeeMoreText';

type Props = {
  unpublishedJob: ApiUnpublishedJob;
};

export function UnpublishedJobPage({ unpublishedJob }: Props) {
  const { i18n } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedJob.id,
  });

  const { hasAts, listingName, id, org } = unpublishedJob;

  useSetDocumentTitle(i18n('Listing Unpublished'));

  useEffect(() => {
    trackListing({
      status: 'unpublished',
      hasAts,
      listingName,
      listingId: id,
      listingType: 'JOB',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [hasAts, id, listingName, org]);

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query });

      navigate(
        searchGetJobsRoutePath({
          lang: CURRENT_LOCALE(),
          q: parsedQuery,
          filtersByName: { locationType },
        }),
        {
          state: {
            searchFrom: 'Unpublished Job Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [navigate],
  );

  // @TODO Add new recommender
  const jobRecommendations: never[] = [];

  const seeMoreText = getUnpublishedJobPageSeeMoreText({
    orgUrl: unpublishedJob.org
      ? `${unpublishedJob.org.url[CURRENT_LOCALE()]}#opportunities`
      : undefined,
    orgPublishedJobsCount: unpublishedJob.orgPublishedJobsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Jobs')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Job Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        jobRecommendations &&
        jobRecommendations.length > 0 && (
          <TileCarousel
            headline={i18n('Explore Similar Jobs on Idealist')}
            results={jobRecommendations}
            seeAll={{
              /* TRANSLATORS: Link to job search */
              text: i18n('See all jobs'),
              action: {
                type: 'link',
                url: searchGetJobsRoutePath({ lang: CURRENT_LOCALE() }),
              },
            }}
            listingType="JOB"
            carouselLocation="Job Listing"
            fromListingId={unpublishedJob.id}
          />
        )
      }
    />
  );
}
